import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Select,
  Space,
  Typography,
  Tag,
  Avatar,
  message,
  Button,
  Tooltip,
} from "antd";
import {
  BankOutlined,
  SwapOutlined,
  StarOutlined,
  StarFilled,
} from "@ant-design/icons";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";

const { Text } = Typography;
const { Option } = Select;

const CompanySwitcher = ({ style = {} }) => {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [favoriteCompanies, setFavoriteCompanies] = useState([]);
  const [recentCompanies, setRecentCompanies] = useState([]);

  const fetchAccessibleCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get("/multi-company/accessible-companies");
      if (response.data.success) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      console.error("Fetch accessible companies error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadFavoriteCompanies = useCallback(() => {
    const saved = localStorage.getItem(`favorite_companies_${user?.id}`);
    if (saved) {
      setFavoriteCompanies(JSON.parse(saved));
    }
  }, [user?.id]);

  const loadRecentCompanies = useCallback(() => {
    const saved = localStorage.getItem(`recent_companies_${user?.id}`);
    if (saved) {
      setRecentCompanies(JSON.parse(saved));
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.role === "multi_company_admin") {
      fetchAccessibleCompanies();
      loadFavoriteCompanies();
      loadRecentCompanies();
    }
  }, [user, loadFavoriteCompanies, loadRecentCompanies]);

  const toggleFavorite = (companyId) => {
    const newFavorites = favoriteCompanies.includes(companyId)
      ? favoriteCompanies.filter((id) => id !== companyId)
      : [...favoriteCompanies, companyId];

    setFavoriteCompanies(newFavorites);
    localStorage.setItem(
      `favorite_companies_${user?.id}`,
      JSON.stringify(newFavorites),
    );
  };

  const addToRecent = (companyId) => {
    const newRecent = [
      companyId,
      ...recentCompanies.filter((id) => id !== companyId),
    ].slice(0, 5);
    setRecentCompanies(newRecent);
    localStorage.setItem(
      `recent_companies_${user?.id}`,
      JSON.stringify(newRecent),
    );
  };

  const handleCompanySwitch = async (companyId) => {
    try {
      setSwitching(true);
      console.log("Company switch triggered:", { companyId, displayCompanies });

      // เพิ่มลงใน recent companies
      addToRecent(companyId);

      // หาข้อมูลบริษัทที่เลือก
      const selectedCompany = displayCompanies.find(
        (c) => c.company_id === companyId,
      );
      console.log("Selected company:", selectedCompany);

      // เรียก API เพื่อสลับบริษัท
      api.defaults.headers["x-company-id"] = companyId;
      console.log("Setting API header x-company-id:", companyId);

      const response = await api.post("/multi-company/switch-company", {
        company_id: companyId,
      });

      if (response.data.success) {
        // อัปเดตข้อมูล user context
        const updatedUser = {
          ...user,
          current_company_id: companyId,
          current_company_name: selectedCompany?.company_name,
          company_permissions: selectedCompany,
        };

        updateUser(updatedUser);

        message.success(`เปลี่ยนไปยัง ${selectedCompany?.company_name} แล้ว`);

        // Trigger a custom event เพื่อให้หน้าอื่นๆ รู้ว่ามีการเปลี่ยนบริษัท
        window.dispatchEvent(
          new CustomEvent("companyChanged", {
            detail: {
              companyId: companyId,
              companyName: selectedCompany?.company_name,
            },
          }),
        );
      }
    } catch (error) {
      console.error("Switch company error:", error);
      message.error("ไม่สามารถเปลี่ยนบริษัทได้");
    } finally {
      setSwitching(false);
    }
  };

  // ใช้ข้อมูลจาก database เท่านั้น
  const displayCompanies = useMemo(() => companies, [companies]);

  // ตั้งค่า current company ID เริ่มต้นถ้ายังไม่มี
  useEffect(() => {
    if (displayCompanies.length > 0 && !user?.current_company_id) {
      const defaultCompany = displayCompanies[0];
      const updatedUser = {
        ...user,
        current_company_id: defaultCompany.company_id,
        current_company_name: defaultCompany.company_name,
        company_permissions: defaultCompany,
      };
      updateUser(updatedUser);
    }
  }, [displayCompanies, user?.current_company_id, updateUser]);

  // จัดเรียงบริษัทตาม access level และชื่อ
  const sortedCompanies = useMemo(() => {
    const sortByLevel = (a, b) => {
      const levelOrder = { owner: 1, admin: 2, manager: 3, viewer: 4 };
      const levelDiff =
        (levelOrder[a.access_level] || 5) - (levelOrder[b.access_level] || 5);

      return levelDiff !== 0
        ? levelDiff
        : a.company_name.localeCompare(b.company_name);
    };

    return [...displayCompanies].sort(sortByLevel); // ✅ clone ก่อน sort
  }, [displayCompanies]);

  const currentCompany = useMemo(
    () =>
      sortedCompanies.find((c) => c.company_id === user?.current_company_id) ||
      sortedCompanies[0],
    [sortedCompanies, user?.current_company_id],
  );

  // แสดง component เฉพาะ multi-company admin ที่มีข้อมูลบริษัท
  const shouldShowComponent =
    user && user.role === "multi_company_admin" && companies.length > 0
    && location.pathname !== '/active-rounds';

  if (!shouldShowComponent) {
    return null;
  }

  return (
    <div style={{ ...style }}>
      <Space align="center" size={8}>
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
          บริษัทปัจจุบัน ({displayCompanies.length} บริษัท):
        </Text>

        {loading && user?.current_company_name ? (
          <div
            style={{
              minWidth: 250,
              padding: "4px 11px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              background: "#f5f5f5",
            }}
          >
            <Space>
              <Avatar size="small" icon={<BankOutlined />} />
              <Text>{user.current_company_name}</Text>
              <Tag color="green" size="small">
                ปัจจุบัน
              </Tag>
            </Space>
          </div>
        ) : (
          <Select
            labelInValue
            value={
              currentCompany
                ? {
                    value: currentCompany.company_id,
                    label: currentCompany.company_name,
                  }
                : undefined
            }
            onChange={(option) => handleCompanySwitch(option.value)}
            loading={loading || switching}
            style={{ minWidth: 250 }}
            placeholder={loading ? "กำลังโหลดข้อมูล..." : "เลือกบริษัท"}
            suffixIcon={<SwapOutlined />}
            optionLabelProp="label"
            showSearch
            notFoundContent={null}
            filterOption={(input, option) => {
              const company = sortedCompanies.find(
                (c) => c.company_id === option.value,
              );
              if (!company) return false;
              return (
                company.company_name
                  .toLowerCase()
                  .includes(input.toLowerCase()) ||
                company.access_level.toLowerCase().includes(input.toLowerCase())
              );
            }}
            popupMatchSelectWidth={false}
            dropdownStyle={{ maxHeight: 400, minWidth: 400 }}
          >
            {/* Render ทุก Option — บริษัทปัจจุบันจะแสดง tag "ปัจจุบัน" อัตโนมัติ */}
            {sortedCompanies.map((company) => {
              const isCurrent =
                String(company.company_id) === String(user?.current_company_id);

              return (
                <Option
                  key={company.company_id}
                  value={company.company_id}
                  label={company.company_name}
                >
                  <Space
                    direction="vertical"
                    size={2}
                    style={{ width: "100%", padding: "4px 0" }}
                  >
                    <Space
                      style={{ width: "100%", justifyContent: "space-between" }}
                    >
                      <Space>
                        <Avatar size="small" icon={<BankOutlined />} />
                        <Text strong>{company.company_name}</Text>
                        {isCurrent && (
                          <Tag color="green" size="small">
                            ปัจจุบัน
                          </Tag>
                        )}
                        {favoriteCompanies.includes(company.company_id) && (
                          <StarFilled style={{ color: "#faad14" }} />
                        )}
                      </Space>
                      <Tooltip
                        title={
                          favoriteCompanies.includes(company.company_id)
                            ? "ลบออกจากรายการโปรด"
                            : "เพิ่มในรายการโปรด"
                        }
                      >
                        <Button
                          type="text"
                          size="small"
                          icon={
                            favoriteCompanies.includes(company.company_id) ? (
                              <StarFilled style={{ color: "#faad14" }} />
                            ) : (
                              <StarOutlined />
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(company.company_id);
                          }}
                        />
                      </Tooltip>
                    </Space>
                    <Space wrap size={4} style={{ marginLeft: 24 }}>
                      <Tag color="blue" size="small">
                        {company.access_level}
                      </Tag>
                      {company.can_manage_users === 1 && (
                        <Tag color="green" size="small">
                          จัดการผู้ใช้
                        </Tag>
                      )}
                      {company.can_manage_checkpoints === 1 && (
                        <Tag color="green" size="small">
                          จุดตรวจ
                        </Tag>
                      )}
                      {company.can_manage_rounds === 1 && (
                        <Tag color="green" size="small">
                          รอบตรวจ
                        </Tag>
                      )}
                      {company.can_view_reports === 1 && (
                        <Tag color="green" size="small">
                          รายงาน
                        </Tag>
                      )}
                    </Space>
                  </Space>
                </Option>
              );
            })}
          </Select>
        )}
      </Space>
    </div>
  );
};

export default CompanySwitcher;
